import { useRouteContext } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { GetAccountResponseDtoMembershipsItemRolePermissions } from '../../../api/generated/model';

type Permissions = GetAccountResponseDtoMembershipsItemRolePermissions;
type PermissionsWithCan = {
  [K in keyof Permissions as NonNullable<Permissions[K]> extends { can: Record<string, boolean> }
    ? K
    : never]: Permissions[K];
};
type PermissionModule = keyof PermissionsWithCan;
// Distributive on purpose: `keyof` over a union of modules would collapse to the actions they all
// share (`never` once `database`, which has no `view`, joins), so map each member separately.
type PermissionAction<M extends PermissionModule> = M extends PermissionModule
  ? keyof NonNullable<PermissionsWithCan[M]>['can'] & string
  : never;

export const usePermissions = () => {
  const { permissions } = useRouteContext({ from: '/_authenticated/_app' });

  const can = useCallback(
    <M extends PermissionModule>(module: M, action: PermissionAction<M>): boolean => {
      const moduleData = permissions?.[module] as { can?: Record<string, boolean> } | undefined;
      return Boolean(moduleData?.can?.[action]);
    },
    [permissions],
  );

  return { permissions, can };
};
